import { useState } from "react";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, Mail, Loader2, ArrowRight, HardHat, ChevronLeft, KeyRound, X } from "lucide-react";
import { toast } from "react-hot-toast";

export default function StudentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- Password Reset States ---
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      setLoading(false);
    } else {
      toast.success("Welcome to Shree Construction Portal");
      navigate("/dashboard");
    }
  };

  // --- Handle Reset Password Request ---
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error("Please enter your work email");
      return;
    }

    setResetLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/update-password`, // URL user is sent to after clicking the email link
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Password reset link sent to your email!");
      setResetModalOpen(false);
      setResetEmail("");
    }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 font-sans">
      {/* --- Background Elements --- */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-[120px] opacity-40"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] bg-red-50 rounded-full blur-[120px] opacity-40"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* --- Back to Home Button --- */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-[#0054A6] mb-6 transition-colors font-bold text-sm group"
        >
          <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          Back to Public Website
        </button>

        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-2xl shadow-blue-900/10 border border-slate-200/50">
          <div className="text-center mb-10">
            {/* --- Branding Icon --- */}
            <div className="h-16 w-16 bg-[#0054A6] rounded-2xl flex items-center justify-center text-white mx-auto mb-6 shadow-lg shadow-blue-200 transform -rotate-3 hover:rotate-0 transition-all cursor-default">
              <HardHat size={32} />
            </div>

            <div className="flex flex-col items-center gap-1">
              <h2 className="text-2xl font-black text-slate-900 tracking-tight uppercase">
                Shree Construction
              </h2>
              <span className="text-[10px] font-black bg-red-600 text-white px-2 py-0.5 rounded tracking-widest uppercase">
                Portal Login
              </span>
            </div>
            <p className="text-slate-500 mt-4 text-sm font-medium">Authorized Personnel Access Only</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {/* --- Email Input --- */}
            <div className="space-y-2 text-left">
              <label className="block text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Work Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-[#0054A6] transition-colors">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  className="block w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-100 text-slate-900 text-sm rounded-xl focus:bg-white focus:border-[#0054A6] transition-all outline-none"
                  placeholder="name@shreehpcl.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* --- Password Input --- */}
            <div className="space-y-2 text-left">
              <div className="flex justify-between items-center px-1">
                <label className="block text-xs font-black uppercase tracking-widest text-slate-400">Password</label>
                {/* Linked Reset Access Button */}
                <button
                  type="button"
                  onClick={() => {
                    setResetEmail(email); // Pre-fill reset email with login email if present
                    setResetModalOpen(true);
                  }}
                  className="text-xs font-bold text-red-600 hover:text-red-700 hover:underline"
                >
                  Reset Access?
                </button>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-[#0054A6] transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  className="block w-full pl-12 pr-12 py-4 bg-slate-50 border-2 border-slate-100 text-slate-900 text-sm rounded-xl focus:bg-white focus:border-[#0054A6] transition-all outline-none"
                  placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* --- Submit Button --- */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0054A6] hover:bg-[#003d7a] text-white font-black py-4 px-4 rounded-xl shadow-lg shadow-blue-100 hover:shadow-blue-200 transition-all flex items-center justify-center gap-3 group disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={24} />
              ) : (
                <>
                  Secure Login
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* --- Bottom Footer --- */}
          <div className="mt-10 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs font-bold text-slate-400">
              Need technical support?{" "}
              <button onClick={() => navigate("/contact")} className="text-[#0054A6] hover:underline ml-1">
                Contact IT Dept
              </button>
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <p className="text-center mt-6 text-[10px] text-slate-400 font-medium uppercase tracking-tighter">
          Encrypted Connection • Site ID: SH-HP-2026
        </p>
      </div>

      {/* --- RESET PASSWORD MODAL --- */}
      {resetModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl relative animate-in zoom-in-95 duration-200">
            {/* Close Modal Button */}
            <button
              onClick={() => setResetModalOpen(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <div className="h-12 w-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-900 uppercase">Reset Password</h3>
              <p className="text-xs text-slate-500 font-medium mt-1">
                Enter your work email to receive a recovery link.
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 ml-1">
                  Work Email
                </label>
                <input
                  type="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="name@shreehpcl.com"
                  className="w-full bg-slate-50 border-2 border-slate-100 font-bold text-sm text-slate-900 rounded-xl p-3.5 focus:bg-white focus:border-[#0054A6] outline-none transition-all"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setResetModalOpen(false)}
                  className="flex-1 py-3 text-slate-500 font-black text-xs uppercase hover:bg-slate-50 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-[2] py-3 bg-[#0054A6] text-white font-black text-xs uppercase rounded-xl shadow-lg shadow-blue-200 hover:bg-[#003d7a] transition flex items-center justify-center gap-2"
                >
                  {resetLoading ? <Loader2 className="animate-spin" size={18} /> : "Send Reset Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}